import { activateKlaviyoEmbed } from './klaviyo-embed.js';

const COUNTRY_REGION_FORM_IDS = new Set(['TSLHLh', 'TU89Pt']);
const CANADIAN_REGIONS = new Set(
  [
    'Alberta',
    'British Columbia',
    'Manitoba',
    'New Brunswick',
    'Newfoundland and Labrador',
    'Northwest Territories',
    'Nova Scotia',
    'Nunavut',
    'Ontario',
    'Prince Edward Island',
    'Quebec',
    'Saskatchewan',
    'Yukon',
  ].map((region) => region.toLowerCase())
);

class KlaviyoFormShell extends HTMLElement {
  #cleanup;
  #frame;
  #observer;
  #timeout;
  #selectedCountry = '';
  #dropdownPreviousValues = new WeakMap();

  connectedCallback() {
    if (this.#cleanup) return;
    this.#cleanup = activateKlaviyoEmbed(this, this.#start);
  }

  #start = () => {
    this.setAttribute('aria-busy', 'true');
    this.addEventListener('input', this.#handlePhoneInput, true);
    this.addEventListener('input', this.#scheduleUpdate, true);
    this.addEventListener('change', this.#scheduleUpdate, true);
    this.addEventListener('click', this.#scheduleUpdate);
    this.addEventListener('keydown', this.#scheduleUpdate);
    this.addEventListener('click', this.#handleDropdownClick);
    this.addEventListener('keydown', this.#handleDropdownKeyDown, true);
    this.addEventListener('focusout', this.#handleDropdownFocusOut, true);

    this.#observer = new MutationObserver(this.#scheduleUpdate);
    this.#observer.observe(this.querySelector('[data-klaviyo-form-id]'), {
      attributes: true,
      attributeFilter: ['class', 'hidden', 'style'],
      childList: true,
      subtree: true,
    });

    this.#timeout = window.setTimeout(() => {
      if (!this.#hasRenderedForm()) this.#showError();
    }, 10000);

    this.#scheduleUpdate();
  };

  disconnectedCallback() {
    this.#cleanup?.();
    this.#cleanup = undefined;
    cancelAnimationFrame(this.#frame);
    this.#frame = undefined;
    this.#observer?.disconnect();
    this.#observer = undefined;
    window.clearTimeout(this.#timeout);
    this.removeEventListener('input', this.#handlePhoneInput, true);
    this.removeEventListener('input', this.#scheduleUpdate, true);
    this.removeEventListener('change', this.#scheduleUpdate, true);
    this.removeEventListener('click', this.#scheduleUpdate);
    this.removeEventListener('keydown', this.#scheduleUpdate);
    this.removeEventListener('click', this.#handleDropdownClick);
    this.removeEventListener('keydown', this.#handleDropdownKeyDown, true);
    this.removeEventListener('focusout', this.#handleDropdownFocusOut, true);
  }

  #hasRenderedForm() {
    return Boolean(this.querySelector('form[data-testid^="klaviyo-form-"], input, select, textarea'));
  }

  #handlePhoneInput = (event) => {
    const input = event.target;

    if (!(input instanceof HTMLInputElement) || !input.matches('input[id*="phone" i]')) return;

    const cursorPosition = input.selectionStart;
    const sanitizedValue = input.value.replace(/[^0-9+(). -]/g, '');

    if (sanitizedValue === input.value) return;

    const sanitizedPrefix = input.value.slice(0, cursorPosition ?? input.value.length).replace(/[^0-9+(). -]/g, '');
    input.value = sanitizedValue;
    input.setSelectionRange(sanitizedPrefix.length, sanitizedPrefix.length);
  };

  #enhancePhoneFields() {
    this.querySelectorAll('input[id*="phone" i]').forEach((input) => {
      input.type = 'tel';
      input.inputMode = 'tel';
      input.autocomplete = 'tel';
      input.pattern = '[0-9+(). -]*';
    });
  }

  #getDropdownByLabel(label) {
    const normalizedLabel = label.toLowerCase();

    for (const labelElement of this.querySelectorAll('[id^="downshift-"][id$="-label"]')) {
      if (!labelElement.textContent.trim().toLowerCase().startsWith(normalizedLabel)) continue;

      const input = document.getElementById(labelElement.id.replace(/-label$/, '-input'));
      if (input instanceof HTMLInputElement && this.contains(input)) return input;
    }
  }

  #isRegionAllowed(region, country) {
    const isCanadianRegion = CANADIAN_REGIONS.has(region.trim().toLowerCase());

    if (country === 'canada') return isCanadianRegion;
    if (country === 'united states') return !isCanadianRegion;

    return true;
  }

  #clearDropdown(input) {
    this.#setDropdownValue(input, '', 'deleteContentBackward');
  }

  #setDropdownValue(input, value, inputType = 'insertText') {
    const valueSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
    valueSetter?.call(input, value);
    input.dispatchEvent(new InputEvent('input', { bubbles: true, inputType, data: value || null }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #getDropdownInput(target) {
    if (!(target instanceof Element)) return;

    const input = target.closest('.klaviyo-dropdown')?.querySelector('[role="combobox"]');
    if (input instanceof HTMLInputElement && this.contains(input)) return input;
  }

  #prepareDropdownForSelection(target) {
    if (target instanceof Element && target.closest('[role="listbox"], [role="option"]')) return;

    const input = this.#getDropdownInput(target);
    if (!input?.value.trim()) return;

    this.#dropdownPreviousValues.set(input, input.value);
    this.#clearDropdown(input);
  }

  #handleDropdownClick = (event) => {
    if (event.button !== 0) return;
    this.#prepareDropdownForSelection(event.target);
  };

  #handleDropdownKeyDown = (event) => {
    const input = this.#getDropdownInput(event.target);
    if (!input) return;

    if (event.key === 'Escape') {
      if (this.#dropdownPreviousValues.has(input)) this.#restoreDropdownValue(input);
      return;
    }

    if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;
    if (!input.value.trim() || input.getAttribute('aria-expanded') === 'true') return;

    const previousValue = input.value;
    this.#dropdownPreviousValues.set(input, previousValue);
    window.setTimeout(() => {
      if (
        input.isConnected &&
        input.value === previousValue &&
        input.getAttribute('aria-expanded') === 'true'
      ) {
        this.#clearDropdown(input);
      }
    });
  };

  #restoreDropdownValue(input) {
    window.setTimeout(() => {
      if (!input.isConnected) return;

      const menuIsOpen = input.getAttribute('aria-expanded') === 'true';
      if (!input.value.trim() && !menuIsOpen) {
        this.#setDropdownValue(input, this.#dropdownPreviousValues.get(input));
      }

      if (input.value.trim() || !menuIsOpen) this.#dropdownPreviousValues.delete(input);
    });
  }

  #handleDropdownFocusOut = (event) => {
    const input = this.#getDropdownInput(event.target);
    if (!input || !this.#dropdownPreviousValues.has(input)) return;

    this.#restoreDropdownValue(input);
  };

  #filterRegionDropdown() {
    if (!COUNTRY_REGION_FORM_IDS.has(this.dataset.formId)) return;

    const countryInput = this.#getDropdownByLabel('country');
    const regionInput = this.#getDropdownByLabel('state');
    if (!countryInput || !regionInput) return;

    const country = (countryInput.value || this.#dropdownPreviousValues.get(countryInput) || '').trim().toLowerCase();
    const isSupportedCountry = country === 'canada' || country === 'united states';
    const countryChanged = isSupportedCountry && this.#selectedCountry && country !== this.#selectedCountry;

    if (isSupportedCountry) this.#selectedCountry = country;

    if (countryChanged && regionInput.value && !this.#isRegionAllowed(regionInput.value, country)) {
      this.#clearDropdown(regionInput);
    }

    const menuId = regionInput.getAttribute('aria-controls') || regionInput.id.replace(/-input$/, '-menu');
    const menu = document.getElementById(menuId);
    if (!menu) return;

    menu.querySelectorAll('[role="option"], [id*="-item-"]').forEach((option) => {
      const hidden = !this.#isRegionAllowed(option.textContent, country);
      option.toggleAttribute('hidden', hidden);
      option.toggleAttribute('data-klaviyo-region-hidden', hidden);
      option.setAttribute('aria-hidden', String(hidden));
    });
  }

  #scheduleUpdate = () => {
    if (this.#frame !== undefined) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = undefined;
      this.#updateState();
    });
  };

  #updateState = () => {
    const rendered = this.#hasRenderedForm();

    const controls = [...this.querySelectorAll('input, select, textarea, button')];
    const hasVisibleControl = controls.some((control) => control.getClientRects().length > 0);

    if (rendered) {
      this.#enhancePhoneFields();
      this.#filterRegionDropdown();
    }

    this.classList.toggle('klaviyo-form-shell--ready', rendered);
    this.classList.toggle('klaviyo-form-shell--success', rendered && !hasVisibleControl);

    if (rendered) {
      window.clearTimeout(this.#timeout);
      if (!this.hasAttribute('data-klaviyo-ready')) {
        this.toggleAttribute('data-klaviyo-transition', !this.querySelector('[data-klaviyo-loading]')?.hidden);
        this.setAttribute('data-klaviyo-ready', '');
      }
      this.setAttribute('aria-busy', 'false');
      this.querySelector('[data-klaviyo-loading]')?.toggleAttribute('hidden', true);
      this.querySelector('[data-klaviyo-error]')?.toggleAttribute('hidden', true);
    }
  };

  #showError() {
    this.setAttribute('aria-busy', 'false');
    this.querySelector('[data-klaviyo-loading]')?.toggleAttribute('hidden', true);
    this.querySelector('[data-klaviyo-error]')?.toggleAttribute('hidden', false);
  }
}

if (!customElements.get('klaviyo-form-shell')) {
  customElements.define('klaviyo-form-shell', KlaviyoFormShell);
}
